class MLService {
    constructor() {
        this.initialized = false;
        this.tf = null;
    }

    async initialize() {
        if (!this.initialized) {
            try {
                // Dynamically import TensorFlow.js
                const tf = await import('@tensorflow/tfjs');
                await import('@tensorflow/tfjs-backend-webgl');
                this.tf = tf;
                await this.tf.ready();
                this.initialized = true;
            } catch (error) {
                console.error('Failed to initialize TensorFlow:', error);
                throw new Error('Failed to initialize TensorFlow');
            }
        }
    }

    // Calculate standard deviation manually since tf.std is not available
    calculateStd(tensor) {
        const mean = this.tf.mean(tensor);
        const squaredDiffs = this.tf.square(this.tf.sub(tensor, mean));
        const variance = this.tf.mean(squaredDiffs);
        return this.tf.sqrt(variance);
    }

    // Normalize a tensor
    normalize(tensor) {
        const mean = this.tf.mean(tensor);
        const std = this.calculateStd(tensor);
        // Add small epsilon to avoid division by zero
        return tensor.sub(mean).div(this.tf.add(std, 1e-7));
    }

    // Preprocess the auction data for training
    preprocessData(auctionData) {
        if (!this.initialized || !this.tf) {
            throw new Error('MLService not initialized');
        }

        const tf = this.tf;
        
        try {
            // Extract numerical features
            const numericalFeatures = auctionData.map(auction => ({
                mileage: parseFloat(auction.miles?.replace(/[^0-9]/g, '') || '0'),
                year: parseInt(auction.year || '0'),
                finalBid: parseFloat(auction.finalBid || '0')
            }));

            // Convert to tensors
            const mileageTensor = tf.tensor1d(numericalFeatures.map(f => f.mileage));
            const yearTensor = tf.tensor1d(numericalFeatures.map(f => f.year));
            const priceTensor = tf.tensor1d(numericalFeatures.map(f => f.finalBid));

            // Calculate statistics
            const stats = {
                mileage: {
                    mean: tf.mean(mileageTensor).arraySync(),
                    std: this.calculateStd(mileageTensor).arraySync()
                },
                year: {
                    mean: tf.mean(yearTensor).arraySync(),
                    std: this.calculateStd(yearTensor).arraySync()
                },
                finalBid: {
                    mean: tf.mean(priceTensor).arraySync(),
                    std: this.calculateStd(priceTensor).arraySync()
                }
            };

            // Get unique categorical values
            const uniqueValues = {
                countries: [...new Set(auctionData.map(a => a.country).filter(Boolean))],
                colors: [...new Set(auctionData.map(a => a.colorFamily).filter(Boolean))],
                interiorColors: [...new Set(auctionData.map(a => a.interiorColor).filter(Boolean))],
                transmissions: [...new Set(auctionData.map(a => a.transmission).filter(Boolean))],
                options: [...new Set(auctionData.flatMap(a => a.options || []).filter(Boolean))],
                conditionTypes: [...new Set(auctionData.map(a => a.conditionType).filter(Boolean))]
            };

            // Ensure we have at least one value for conditionTypes to avoid empty arrays
            if (uniqueValues.conditionTypes.length === 0) {
                uniqueValues.conditionTypes = ['Standard'];
            }

            // Create feature tensors
            const features = auctionData.map(auction => {
                // Normalize numerical features
                const normalizedMileage = (parseFloat(auction.miles?.replace(/[^0-9]/g, '') || '0') - stats.mileage.mean) / (stats.mileage.std + 1e-7);
                const normalizedYear = (parseInt(auction.year || '0') - stats.year.mean) / (stats.year.std + 1e-7);

                // One-hot encode categorical features
                const countryEncoding = uniqueValues.countries.map(c => auction.country === c ? 1 : 0);
                const colorEncoding = uniqueValues.colors.map(c => auction.colorFamily === c ? 1 : 0);
                const interiorEncoding = uniqueValues.interiorColors.map(c => auction.interiorColor === c ? 1 : 0);
                const transmissionEncoding = uniqueValues.transmissions.map(t => auction.transmission === t ? 1 : 0);
                const optionsEncoding = uniqueValues.options.map(o => auction.options?.includes(o) ? 1 : 0);
                
                // Handle conditionType - if not present, use the first value as default
                const conditionType = auction.conditionType || uniqueValues.conditionTypes[0];
                const conditionTypeEncoding = uniqueValues.conditionTypes.map(ct => conditionType === ct ? 1 : 0);

                // Boolean features
                const accident = auction.accident ? 1 : 0;

                return [
                    normalizedMileage,
                    normalizedYear,
                    accident,
                    ...conditionTypeEncoding,
                    ...countryEncoding,
                    ...colorEncoding,
                    ...interiorEncoding,
                    ...transmissionEncoding,
                    ...optionsEncoding
                ];
            });

            // Convert to tensors
            const featureTensor = tf.tensor2d(features);
            const labelTensor = this.normalize(priceTensor);

            // Clean up intermediate tensors
            mileageTensor.dispose();
            yearTensor.dispose();
            priceTensor.dispose();

            return {
                features: featureTensor,
                labels: labelTensor,
                stats,
                uniqueValues
            };
        } catch (error) {
            console.error('Error in preprocessData:', error);
            throw new Error(`Data preprocessing failed: ${error.message}`);
        }
    }

    // Create the TensorFlow.js model
    createModel(numFeatures) {
        if (!this.initialized || !this.tf) {
            throw new Error('MLService not initialized');
        }

        const tf = this.tf;
        const model = tf.sequential();
        
        // Input layer with batch normalization
        model.add(tf.layers.dense({
            inputShape: [numFeatures],
            units: 128,
            kernelInitializer: 'glorotNormal'
        }));
        model.add(tf.layers.batchNormalization());
        model.add(tf.layers.activation({ activation: 'relu' }));
        model.add(tf.layers.dropout({ rate: 0.3 }));
        
        // Hidden layers
        model.add(tf.layers.dense({
            units: 64,
            kernelInitializer: 'glorotNormal'
        }));
        model.add(tf.layers.batchNormalization());
        model.add(tf.layers.activation({ activation: 'relu' }));
        model.add(tf.layers.dropout({ rate: 0.2 }));
        
        model.add(tf.layers.dense({
            units: 32,
            kernelInitializer: 'glorotNormal'
        }));
        model.add(tf.layers.batchNormalization());
        model.add(tf.layers.activation({ activation: 'relu' }));
        
        // Output layer
        model.add(tf.layers.dense({
            units: 1,
            kernelInitializer: 'glorotNormal'
        }));

        // Use Adam optimizer with a lower learning rate
        const optimizer = tf.train.adam(0.0005);
        
        // Compile the model with MSE loss only
        model.compile({
            optimizer: optimizer,
            loss: 'meanSquaredError'
        });

        return model;
    }

    // Train the model
    async trainModel(model, features, labels, epochs = 100) {
        if (!this.initialized || !this.tf) {
            throw new Error('MLService not initialized');
        }

        try {
            // Check if features and labels are valid
            if (!features || !labels) {
                throw new Error('Features or labels are missing');
            }

            console.log('Training model with:', {
                featuresShape: features.shape,
                labelsShape: labels.shape,
                epochs,
                batchSize: 32
            });

            // Normalize labels to improve training stability
            const meanPrice = this.tf.mean(labels);
            const stdPrice = this.calculateStd(labels);
            const normalizedLabels = labels.sub(meanPrice).div(this.tf.add(stdPrice, 1e-7));

            // Store normalization parameters in the model for prediction
            model.meanPrice = meanPrice;
            model.stdPrice = stdPrice;

            const result = await model.fit(features, normalizedLabels, {
                epochs,
                validationSplit: 0.2,
                shuffle: true,
                batchSize: 32,
                callbacks: {
                    onEpochBegin: async (epoch) => {
                        console.log(`Starting epoch ${epoch + 1}/${epochs}`);
                    },
                    onEpochEnd: async (epoch, logs) => {
                        console.log(`Epoch ${epoch + 1}: loss = ${logs.loss.toFixed(4)}, val_loss = ${logs.val_loss?.toFixed(4) || 'N/A'}`);
                    }
                }
            });

            return result;
        } catch (error) {
            console.error('Error during model training:', error);
            console.error('Features shape:', features?.shape);
            console.error('Labels shape:', labels?.shape);
            throw new Error(`Training failed: ${error.message}`);
        }
    }

    // Make a prediction
    async predictPrice(model, input, stats, uniqueValues) {
        if (!this.initialized || !this.tf) {
            throw new Error('MLService not initialized');
        }

        try {
            const tf = this.tf;

            // Prepare input features in the same format as training data
            const normalizedMileage = (parseFloat(input.mileage) - stats.mileage.mean) / (stats.mileage.std + 1e-7);
            const normalizedYear = (parseInt(input.year) - stats.year.mean) / (stats.year.std + 1e-7);

            // One-hot encode categorical features
            const countryEncoding = uniqueValues.countries.map(c => input.country === c ? 1 : 0);
            const colorEncoding = uniqueValues.colors.map(c => input.color === c ? 1 : 0);
            const interiorEncoding = uniqueValues.interiorColors.map(c => input.interiorColor === c ? 1 : 0);
            const transmissionEncoding = uniqueValues.transmissions.map(t => input.transmission === t ? 1 : 0);
            const optionsEncoding = uniqueValues.options.map(o => (input.options || []).includes(o) ? 1 : 0);
            
            // Handle conditionType - if not present, use the first value as default
            const conditionType = input.conditionType || uniqueValues.conditionTypes[0];
            const conditionTypeEncoding = uniqueValues.conditionTypes.map(ct => conditionType === ct ? 1 : 0);

            // Boolean features
            const accident = input.accident === 'No' ? 0 : 1;

            // Combine all features
            const inputFeatures = [
                normalizedMileage,
                normalizedYear,
                accident,
                ...conditionTypeEncoding,
                ...countryEncoding,
                ...colorEncoding,
                ...interiorEncoding,
                ...transmissionEncoding,
                ...optionsEncoding
            ];

            // Debug input features
            console.log('Prediction input:', {
                normalizedMileage,
                normalizedYear,
                accident,
                conditionType: input.conditionType,
                conditionTypeEncoding,
                countryEncoding,
                colorEncoding,
                interiorEncoding,
                transmissionEncoding,
                optionsEncoding,
                totalFeatures: inputFeatures.length
            });

            // Make prediction
            const normalizedPrediction = await model.predict(tf.tensor2d([inputFeatures]));
            const predictionData = await normalizedPrediction.data();
            
            // Denormalize prediction using the stored parameters
            const finalPrediction = predictionData[0] * stats.finalBid.std + stats.finalBid.mean;

            // Clean up tensors
            normalizedPrediction.dispose();

            // Ensure prediction is positive
            return Math.max(finalPrediction, 0);
        } catch (error) {
            console.error('Error during prediction:', error);
            console.error('Input:', input);
            console.error('Stats:', stats);
            console.error('UniqueValues:', uniqueValues);
            throw new Error(`Prediction failed: ${error.message}`);
        }
    }
}

// Create and export a singleton instance
const mlService = new MLService();
export default mlService;
